import { useEffect, useState } from 'react'
import { Plus, Pencil, Trash2, Loader2, ToggleLeft, ToggleRight } from 'lucide-react'
import { useAdmin } from '../../hooks/use-admin'
import { useI18n } from '../../i18n'
import type { AdminPlan, AdminPlanCreateRequest, AdminPlanUpdateRequest } from '../../types'

export function PlanSection() {
  const { plans, loading, fetchPlans, createPlan, updatePlan, deletePlan, togglePlan } = useAdmin()
  const { t } = useI18n()

  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<AdminPlan | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<AdminPlan | null>(null)

  const emptyForm: AdminPlanCreateRequest = {
    planCode: '',
    name: '',
    priceMonthly: 0,
    priceYearly: null,
    tokenQuota: 0,
    maxUsers: 1,
    features: '',
    description: '',
    sortOrder: 0,
    status: 1,
  }
  const [form, setForm] = useState<AdminPlanCreateRequest>(emptyForm)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    fetchPlans()
  }, [fetchPlans])

  const openCreate = () => {
    setEditing(null)
    setForm(emptyForm)
    setDialogOpen(true)
  }

  const openEdit = (plan: AdminPlan) => {
    setEditing(plan)
    setForm({
      planCode: plan.planCode,
      name: plan.name,
      priceMonthly: plan.priceMonthly,
      priceYearly: plan.priceYearly,
      tokenQuota: plan.tokenQuota,
      maxUsers: plan.maxUsers,
      features: plan.features,
      description: plan.description,
      sortOrder: plan.sortOrder,
      status: plan.status,
    })
    setDialogOpen(true)
  }

  const handleSubmit = async () => {
    if (!form.planCode || !form.name) return
    setSubmitting(true)
    let ok = false
    if (editing) {
      ok = await updatePlan({ id: editing.id, ...form })
    } else {
      ok = await createPlan(form)
    }
    setSubmitting(false)
    if (ok) {
      setDialogOpen(false)
      fetchPlans()
    }
  }

  const handleToggle = async (plan: AdminPlan) => {
    const ok = await togglePlan(plan.id)
    if (ok) fetchPlans()
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    const ok = await deletePlan(deleteTarget.id)
    if (ok) fetchPlans()
    setDeleteTarget(null)
  }

  const updateForm = (key: keyof AdminPlanCreateRequest, value: any) => {
    setForm((f) => ({ ...f, [key]: value }))
  }

  const formatQuota = (n: number) => {
    if (n >= 10000) return `${(n / 10000).toFixed(n % 10000 === 0 ? 0 : 1)}万`
    return n.toLocaleString()
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium text-[#1a1a1a]">
          {t('套餐管理', 'Plan Management')}
        </h2>
        <button
          onClick={openCreate}
          className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] rounded-xl bg-orange-500 text-white hover:bg-orange-600 transition-all"
        >
          <Plus className="h-3.5 w-3.5" />
          {t('新增套餐', 'Create Plan')}
        </button>
      </div>

      {/* Plan list */}
      {loading && plans.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-5 w-5 animate-spin text-orange-500" />
        </div>
      ) : plans.length === 0 ? (
        <div className="text-center py-16 text-sm text-gray-400">
          {t('暂无套餐', 'No plans')}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`p-4 rounded-2xl border transition-all group ${
                plan.status === 1
                  ? 'bg-white border-black/[0.04]'
                  : 'bg-gray-50 border-black/[0.04] opacity-60'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-[15px] font-semibold text-[#1a1a1a]">{plan.name}</span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-black/[0.04] text-gray-400 font-mono">
                      {plan.planCode}
                    </span>
                    {plan.status === 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-red-50 text-red-400">
                        {t('下架', 'Off')}
                      </span>
                    )}
                  </div>
                  {plan.description && (
                    <p className="text-[12px] text-gray-400 mt-0.5">{plan.description}</p>
                  )}
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                  <button
                    onClick={() => handleToggle(plan)}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-orange-500 hover:bg-orange-50 transition-all"
                    title={plan.status === 1 ? t('下架', 'Disable') : t('上架', 'Enable')}
                  >
                    {plan.status === 1
                      ? <ToggleRight className="h-4 w-4 text-emerald-500" />
                      : <ToggleLeft className="h-4 w-4" />
                    }
                  </button>
                  <button
                    onClick={() => openEdit(plan)}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-[#1a1a1a] hover:bg-black/[0.04] transition-all"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => setDeleteTarget(plan)}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              <div className="flex items-baseline gap-1 mb-3">
                <span className="text-2xl font-bold text-orange-500">¥{plan.priceMonthly}</span>
                <span className="text-[11px] text-gray-400">/{t('月', 'mo')}</span>
                {plan.priceYearly != null && (
                  <>
                    <span className="text-[11px] text-gray-300 mx-1">|</span>
                    <span className="text-sm font-medium text-gray-500">¥{plan.priceYearly}</span>
                    <span className="text-[11px] text-gray-400">/{t('年', 'yr')}</span>
                  </>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="px-2.5 py-1.5 rounded-lg bg-black/[0.02]">
                  <span className="text-[10px] text-gray-400">{t('Token额度', 'Tokens')}</span>
                  <p className="text-[13px] font-medium text-[#1a1a1a] mt-0.5">{formatQuota(plan.tokenQuota)}</p>
                </div>
                <div className="px-2.5 py-1.5 rounded-lg bg-black/[0.02]">
                  <span className="text-[10px] text-gray-400">{t('最大用户', 'Max Users')}</span>
                  <p className="text-[13px] font-medium text-[#1a1a1a] mt-0.5">{plan.maxUsers}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit dialog */}
      {dialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setDialogOpen(false)} />
          <div className="relative bg-white rounded-2xl border border-black/[0.06] shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-black/[0.04]">
              <h3 className="text-sm font-semibold text-[#1a1a1a]">
                {editing ? t('编辑套餐', 'Edit Plan') : t('新增套餐', 'Create Plan')}
              </h3>
            </div>

            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] text-gray-400 mb-1">planCode *</label>
                  <input
                    value={form.planCode}
                    onChange={(e) => updateForm('planCode', e.target.value)}
                    disabled={!!editing}
                    className="w-full h-9 text-sm px-3 rounded-xl bg-white/60 border border-black/[0.06] focus:border-orange-200 focus:outline-none disabled:opacity-50"
                    placeholder="BASIC"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-400 mb-1">{t('名称', 'Name')} *</label>
                  <input
                    value={form.name}
                    onChange={(e) => updateForm('name', e.target.value)}
                    className="w-full h-9 text-sm px-3 rounded-xl bg-white/60 border border-black/[0.06] focus:border-orange-200 focus:outline-none"
                    placeholder={t('基础版', 'Basic')}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] text-gray-400 mb-1">{t('月价', 'Monthly Price')} *</label>
                  <input
                    type="number"
                    value={form.priceMonthly}
                    onChange={(e) => updateForm('priceMonthly', Number(e.target.value))}
                    className="w-full h-9 text-sm px-3 rounded-xl bg-white/60 border border-black/[0.06] focus:border-orange-200 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-400 mb-1">{t('年价', 'Yearly Price')}</label>
                  <input
                    type="number"
                    value={form.priceYearly ?? ''}
                    onChange={(e) => updateForm('priceYearly', e.target.value ? Number(e.target.value) : null)}
                    className="w-full h-9 text-sm px-3 rounded-xl bg-white/60 border border-black/[0.06] focus:border-orange-200 focus:outline-none"
                    placeholder={t('可选', 'Optional')}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] text-gray-400 mb-1">{t('Token额度', 'Token Quota')} *</label>
                  <input
                    type="number"
                    value={form.tokenQuota}
                    onChange={(e) => updateForm('tokenQuota', Number(e.target.value))}
                    className="w-full h-9 text-sm px-3 rounded-xl bg-white/60 border border-black/[0.06] focus:border-orange-200 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-400 mb-1">{t('最大用户', 'Max Users')} *</label>
                  <input
                    type="number"
                    value={form.maxUsers}
                    onChange={(e) => updateForm('maxUsers', Number(e.target.value))}
                    className="w-full h-9 text-sm px-3 rounded-xl bg-white/60 border border-black/[0.06] focus:border-orange-200 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] text-gray-400 mb-1">{t('功能特性', 'Features')}</label>
                <textarea
                  value={form.features}
                  onChange={(e) => updateForm('features', e.target.value)}
                  rows={2}
                  className="w-full text-sm px-3 py-2 rounded-xl bg-white/60 border border-black/[0.06] focus:border-orange-200 focus:outline-none resize-none"
                  placeholder={t('每行一个特性', 'One feature per line')}
                />
              </div>

              <div>
                <label className="block text-[11px] text-gray-400 mb-1">{t('描述', 'Description')}</label>
                <input
                  value={form.description}
                  onChange={(e) => updateForm('description', e.target.value)}
                  className="w-full h-9 text-sm px-3 rounded-xl bg-white/60 border border-black/[0.06] focus:border-orange-200 focus:outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] text-gray-400 mb-1">sortOrder</label>
                  <input
                    type="number"
                    value={form.sortOrder ?? 0}
                    onChange={(e) => updateForm('sortOrder', Number(e.target.value))}
                    className="w-full h-9 text-sm px-3 rounded-xl bg-white/60 border border-black/[0.06] focus:border-orange-200 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-400 mb-1">status</label>
                  <select
                    value={form.status ?? 1}
                    onChange={(e) => updateForm('status', Number(e.target.value))}
                    className="w-full h-9 text-sm px-3 rounded-xl bg-white/60 border border-black/[0.06] focus:border-orange-200 focus:outline-none"
                  >
                    <option value={1}>{t('上架', 'Active')}</option>
                    <option value={0}>{t('下架', 'Inactive')}</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 p-4 border-t border-black/[0.04]">
              <button
                onClick={() => setDialogOpen(false)}
                className="px-4 py-2 text-[13px] rounded-xl bg-black/[0.04] text-gray-400 hover:text-[#1a1a1a] transition-all"
              >
                {t('取消', 'Cancel')}
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || !form.planCode || !form.name}
                className="px-4 py-2 text-[13px] rounded-xl bg-orange-500 text-white hover:bg-orange-600 transition-all disabled:opacity-50"
              >
                {submitting ? '...' : t('确认', 'Confirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={() => setDeleteTarget(null)} />
          <div className="relative bg-white rounded-2xl border border-black/[0.06] shadow-xl w-full max-w-sm mx-4 p-5">
            <h3 className="text-sm font-semibold text-[#1a1a1a] mb-2">
              {t('确认删除', 'Confirm Delete')}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {t('确定要删除套餐', 'Delete plan')} <span className="text-orange-600 font-medium">{deleteTarget.name}</span>？
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 text-[13px] rounded-xl bg-black/[0.04] text-gray-400 hover:text-[#1a1a1a] transition-all"
              >
                {t('取消', 'Cancel')}
              </button>
              <button
                onClick={handleDelete}
                className="px-4 py-2 text-[13px] rounded-xl bg-red-500 text-white hover:bg-red-600 transition-all"
              >
                {t('删除', 'Delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
